import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Users,
  Send,
  UserCheck,
  UserX,
  Clock,
  Search,
  Filter,
  Activity,
  MessageSquare,
  Mail,
  Briefcase,
  Eye,
} from "lucide-react";
import { Tab } from "@headlessui/react";
import CandidateProfileView from "../../../Candidate/Components/CandidateProfileView";
import {
  getCandidateById,
  getInvitationStatsByCandidiateId,
  getInvitationsByInvitedUserFiltered,
} from "../../../Candidate/Services/CandidateService";
import {
  AllInvitationByInvitedUserResponseDTO,
  CandidateInvitationStats,
  CandidateProfile,
  InvitationStats,
} from "../../../Candidate/Types/CandidateType";
import { getUserFeedbackFiltered } from "../../../Admin/Services/AdminService";
import { FeedbackResponseDto, FeedbackStats } from "../../../Admin/Types/FeedbackType";
import {
  getSmartAnalyticsCandidateActivities,
  getSmartAnalyticsCandidateInvitations,
} from "../../Services/SmartAnalyticsService";
import {
  SmartAnalyticsCandidateActivityRow,
  SmartAnalyticsCandidateInvitationRow,
} from "../../Types/SmartAnalyticsType";
import Pagination from "../../../Shared/Pagination/Pagination";
import { PagedStatsResponse, PaginationResponse } from "../../../Shared/Pagination/PaginationResponse";
import { formatDateTime } from "../../../Shared/Utils/HelperUtils";
import { EmptyState, fieldClass, LoadingRows, PAGE_SIZE, statusBadge } from "./SmartAnalyticsShared";

const toUuid = (id: string): string => {
  const hex = (id || "").replace(/-/g, "").trim();
  if (hex.length !== 32) return id;
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
};

const tabButton = (selected: boolean) =>
  `rounded-xl px-4 py-2 text-xs font-black uppercase tracking-wider transition ${
    selected
      ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30"
      : "text-slate-400 hover:text-slate-200 border border-transparent"
  }`;

const SectionHeader = ({ title, children }: { title: string; children?: React.ReactNode }) => (
  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
    <h3 className="text-xs font-black uppercase tracking-widest text-slate-200">{title}</h3>
    {children}
  </div>
);

const SmartAnalyticsCandidateComponent = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const uuid = userId ? toUuid(userId) : "";

  const [profile, setProfile] = useState<CandidateProfile | null>(null);
  const [invitationStats, setInvitationStats] = useState<CandidateInvitationStats | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [activeTab, setActiveTab] = useState(0);

  // Activities
  const [activities, setActivities] = useState<PaginationResponse<SmartAnalyticsCandidateActivityRow> | null>(null);
  const [activitySearch, setActivitySearch] = useState("");
  const [activityPage, setActivityPage] = useState(0);
  const [activityLoading, setActivityLoading] = useState(false);

  // Received invitations
  const [invitations, setInvitations] = useState<PaginationResponse<SmartAnalyticsCandidateInvitationRow> | null>(null);
  const [invitationSearch, setInvitationSearch] = useState("");
  const [invitationStatus, setInvitationStatus] = useState("");
  const [invitationPage, setInvitationPage] = useState(0);
  const [invitationLoading, setInvitationLoading] = useState(false);

  // Feedback
  const [feedback, setFeedback] = useState<PagedStatsResponse<FeedbackResponseDto, FeedbackStats> | null>(null);
  const [feedbackSearch, setFeedbackSearch] = useState("");
  const [feedbackStatus, setFeedbackStatus] = useState("");
  const [feedbackPage, setFeedbackPage] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Referrals (invite links the candidate shared)
  const [referrals, setReferrals] = useState<PagedStatsResponse<AllInvitationByInvitedUserResponseDTO, InvitationStats> | null>(null);
  const [referralSearch, setReferralSearch] = useState("");
  const [referralStatus, setReferralStatus] = useState("");
  const [referralPage, setReferralPage] = useState(0);
  const [referralLoading, setReferralLoading] = useState(false);

  useEffect(() => {
    if (!uuid) return;
    let cancelled = false;
    setProfileLoading(true);
    Promise.all([getCandidateById(uuid), getInvitationStatsByCandidiateId(uuid)])
      .then(([profileRes, statsRes]) => {
        if (cancelled) return;
        setProfile(profileRes?.data ?? null);
        setInvitationStats(statsRes?.data ?? null);
      })
      .finally(() => {
        if (!cancelled) setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  const loadActivities = useCallback(async () => {
    if (!uuid) return;
    setActivityLoading(true);
    const response = await getSmartAnalyticsCandidateActivities(uuid, activitySearch || undefined, activityPage, PAGE_SIZE);
    if (response.success) setActivities(response.data);
    setActivityLoading(false);
  }, [uuid, activitySearch, activityPage]);

  const loadInvitations = useCallback(async () => {
    if (!uuid) return;
    setInvitationLoading(true);
    const response = await getSmartAnalyticsCandidateInvitations(
      uuid,
      invitationSearch || undefined,
      invitationStatus || undefined,
      invitationPage,
      PAGE_SIZE
    );
    if (response.success) setInvitations(response.data);
    setInvitationLoading(false);
  }, [uuid, invitationSearch, invitationStatus, invitationPage]);

  const loadFeedback = useCallback(async () => {
    if (!uuid) return;
    setFeedbackLoading(true);
    const response = await getUserFeedbackFiltered(
      uuid,
      {
        search: feedbackSearch || undefined,
        status: feedbackStatus || undefined,
      },
      feedbackPage,
      PAGE_SIZE
    );
    if (response.success) setFeedback(response.data);
    setFeedbackLoading(false);
  }, [uuid, feedbackSearch, feedbackStatus, feedbackPage]);

  const loadReferrals = useCallback(async () => {
    if (!uuid) return;
    setReferralLoading(true);
    const response = await getInvitationsByInvitedUserFiltered(
      uuid,
      {
        search: referralSearch || undefined,
        status: referralStatus || undefined,
      },
      referralPage,
      PAGE_SIZE
    );
    if (response.success) setReferrals(response.data);
    setReferralLoading(false);
  }, [uuid, referralSearch, referralStatus, referralPage]);

  useEffect(() => {
    if (activeTab === 1) loadActivities();
  }, [activeTab, loadActivities]);

  useEffect(() => {
    if (activeTab === 2) loadInvitations();
  }, [activeTab, loadInvitations]);

  useEffect(() => {
    if (activeTab === 3) loadFeedback();
  }, [activeTab, loadFeedback]);

  useEffect(() => {
    if (activeTab === 4) loadReferrals();
  }, [activeTab, loadReferrals]);

  if (profileLoading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-slate-500">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-cyan-400" />
        <p className="text-xs font-bold uppercase tracking-widest">Loading candidate dashboard...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-10">
        <EmptyState
          icon={<User className="h-6 w-6" />}
          title="Candidate not found"
          body="We couldn't load the profile for this candidate."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
        <button
          onClick={() => navigate("/admin/smart-analytics")}
          className="mb-4 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-slate-400 transition hover:text-cyan-400"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to analytics
        </button>

        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-800 bg-slate-950">
              {profile.profilePhotoUrl ? (
                <img src={profile.profilePhotoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <User className="h-8 w-8 text-slate-600" />
              )}
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-100">
                {profile.firstName || profile.lastName
                  ? `${profile.firstName || ""} ${profile.lastName || ""}`.trim()
                  : profile.userEmail}
              </h1>
              <p className="text-sm font-semibold text-slate-500">{profile.userEmail}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {statusBadge(profile.userStatus)}
                {profile.availabilityStatus && (
                  <span className="rounded-full border border-slate-700 bg-slate-950/60 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
                    {profile.availabilityStatus.replace(/_/g, " ").toLowerCase()}
                  </span>
                )}
                <span className="rounded-full border border-slate-700 bg-slate-950/60 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {profile.totalYearsOfExperience ?? 0} years exp
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-center">
              <p className="text-xs font-semibold text-slate-500">Received</p>
              <p className="mt-1 text-2xl font-black text-cyan-400">{invitationStats?.receivedInvitationCount ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-center">
              <p className="text-xs font-semibold text-slate-500">Accepted</p>
              <p className="mt-1 text-2xl font-black text-emerald-400">{invitationStats?.acceptedInvitationCount ?? 0}</p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 text-center">
              <p className="text-xs font-semibold text-slate-500">Rejected</p>
              <p className="mt-1 text-2xl font-black text-amber-400">{invitationStats?.rejectedInvitationCount ?? 0}</p>
            </div>
          </div>
        </div>
      </div>

      <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
        <Tab.List className="flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 p-2">
          <Tab className={({ selected }) => tabButton(selected)}>Profile</Tab>
          <Tab className={({ selected }) => tabButton(selected)}>Activity</Tab>
          <Tab className={({ selected }) => tabButton(selected)}>Invitations</Tab>
          <Tab className={({ selected }) => tabButton(selected)}>Feedback</Tab>
          <Tab className={({ selected }) => tabButton(selected)}>Referrals</Tab>
        </Tab.List>

        <Tab.Panels>
          <Tab.Panel>
            <CandidateProfileView data={profile} />
          </Tab.Panel>

          <Tab.Panel>
            <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
              <SectionHeader title="Candidate activity">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                  <input
                    value={activitySearch}
                    onChange={(event) => {
                      setActivitySearch(event.target.value);
                      setActivityPage(0);
                    }}
                    placeholder="Search activity..."
                    className={`${fieldClass} w-64 pl-9`}
                  />
                </div>
              </SectionHeader>

              {activityLoading ? (
                <table className="w-full">
                  <tbody>
                    <LoadingRows rows={3} cols={4} />
                  </tbody>
                </table>
              ) : !activities || activities.content.length === 0 ? (
                <EmptyState
                  icon={<Activity className="h-6 w-6" />}
                  title="No activity"
                  body="This candidate has no recorded activity yet."
                />
              ) : (
                <>
                  <table className="w-full">
                    <thead className="border-y border-slate-800 bg-slate-900/60">
                      <tr>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Event</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Message</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Actor</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activities.content.map((activity) => (
                        <tr key={activity.activityId} className="border-t border-slate-800/70 transition hover:bg-slate-900/40">
                          <td className="px-5 py-4">
                            <p className="text-xs font-bold text-slate-200">{activity.eventType || "-"}</p>
                            <p className="text-[10px] font-semibold text-slate-500">{activity.category || "-"}</p>
                          </td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{activity.message || "-"}</td>
                          <td className="px-5 py-4">
                            <p className="text-xs font-semibold text-slate-200">{activity.actorName || "-"}</p>
                            <p className="text-[10px] font-semibold text-slate-500">{activity.actorRole || "-"}</p>
                          </td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                            {formatDateTime(activity.createdAt ?? undefined)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {activities.totalPages > 1 && (
                    <div className="mt-4">
                      <Pagination {...activities} setPage={setActivityPage} />
                    </div>
                  )}
                </>
              )}
            </div>
          </Tab.Panel>

          <Tab.Panel>
            <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
              <SectionHeader title="Received invitations">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                    <input
                      value={invitationSearch}
                      onChange={(event) => {
                        setInvitationSearch(event.target.value);
                        setInvitationPage(0);
                      }}
                      placeholder="Search job, recruiter, company..."
                      className={`${fieldClass} w-64 pl-9`}
                    />
                  </div>
                  <select
                    value={invitationStatus}
                    onChange={(event) => {
                      setInvitationStatus(event.target.value);
                      setInvitationPage(0);
                    }}
                    className={fieldClass}
                  >
                    <option value="">All statuses</option>
                    <option value="PENDING">Pending</option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
              </SectionHeader>

              {invitationLoading ? (
                <table className="w-full">
                  <tbody>
                    <LoadingRows rows={3} cols={5} />
                  </tbody>
                </table>
              ) : !invitations || invitations.content.length === 0 ? (
                <EmptyState
                  icon={<Send className="h-6 w-6" />}
                  title="No invitations"
                  body="This candidate has not received any invitations yet."
                />
              ) : (
                <>
                  <table className="w-full">
                    <thead className="border-y border-slate-800 bg-slate-900/60">
                      <tr>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Job / Company</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Recruiter</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Invited</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Responded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.content.map((invitation) => (
                        <tr key={invitation.invitationId} className="border-t border-slate-800/70 transition hover:bg-slate-900/40">
                          <td className="px-5 py-4">
                            <p className="text-xs font-bold text-slate-200">{invitation.jobTitle || "-"}</p>
                            <p className="text-[10px] font-semibold text-slate-500">{invitation.companyName || "-"}</p>
                          </td>
                          <td className="px-5 py-4">
                            <p className="text-xs font-semibold text-slate-200">{invitation.recruiterName || "-"}</p>
                            <p className="text-[10px] font-semibold text-slate-500">{invitation.recruiterEmail || "-"}</p>
                          </td>
                          <td className="px-5 py-4">{statusBadge(invitation.status)}</td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                            {formatDateTime(invitation.invitedAt ?? undefined)}
                          </td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                            {formatDateTime(invitation.respondedAt ?? undefined)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {invitations.totalPages > 1 && (
                    <div className="mt-4">
                      <Pagination {...invitations} setPage={setInvitationPage} />
                    </div>
                  )}
                </>
              )}
            </div>
          </Tab.Panel>

          <Tab.Panel>
            <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
              <SectionHeader title="Feedback">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                    <input
                      value={feedbackSearch}
                      onChange={(event) => {
                        setFeedbackSearch(event.target.value);
                        setFeedbackPage(0);
                      }}
                      placeholder="Search feedback..."
                      className={`${fieldClass} w-64 pl-9`}
                    />
                  </div>
                  <select
                    value={feedbackStatus}
                    onChange={(event) => {
                      setFeedbackStatus(event.target.value);
                      setFeedbackPage(0);
                    }}
                    className={fieldClass}
                  >
                    <option value="">All statuses</option>
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In progress</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option>
                    <option value="REOPEN">Reopen</option>
                  </select>
                </div>
              </SectionHeader>

              {feedbackLoading ? (
                <table className="w-full">
                  <tbody>
                    <LoadingRows rows={3} cols={4} />
                  </tbody>
                </table>
              ) : !feedback || feedback.page.content.length === 0 ? (
                <EmptyState
                  icon={<MessageSquare className="h-6 w-6" />}
                  title="No feedback"
                  body="This candidate has not submitted any feedback yet."
                />
              ) : (
                <>
                  <table className="w-full">
                    <thead className="border-y border-slate-800 bg-slate-900/60">
                      <tr>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Subject</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Category</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feedback.page.content.map((item) => (
                        <tr key={item.id} className="border-t border-slate-800/70 transition hover:bg-slate-900/40">
                          <td className="px-5 py-4">
                            <p className="text-xs font-bold text-slate-200">{item.subject || "-"}</p>
                            <p className="text-[10px] font-semibold text-slate-500 line-clamp-2">{item.message || "-"}</p>
                          </td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-400">{item.category || "-"}</td>
                          <td className="px-5 py-4">{statusBadge(item.status)}</td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                            {formatDateTime(item.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {feedback.page.totalPages > 1 && (
                    <div className="mt-4">
                      <Pagination {...feedback.page} setPage={setFeedbackPage} />
                    </div>
                  )}
                </>
              )}
            </div>
          </Tab.Panel>

          <Tab.Panel>
            <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
              <SectionHeader title="Referrals sent">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                    <input
                      value={referralSearch}
                      onChange={(event) => {
                        setReferralSearch(event.target.value);
                        setReferralPage(0);
                      }}
                      placeholder="Search invitee..."
                      className={`${fieldClass} w-64 pl-9`}
                    />
                  </div>
                  <select
                    value={referralStatus}
                    onChange={(event) => {
                      setReferralStatus(event.target.value);
                      setReferralPage(0);
                    }}
                    className={fieldClass}
                  >
                    <option value="">All statuses</option>
                    <option value="REGISTERED">Registered</option>
                    <option value="NOT_REGISTERED">Not registered</option>
                    <option value="PENDING">Pending</option>
                  </select>
                </div>
              </SectionHeader>

              {referralLoading ? (
                <table className="w-full">
                  <tbody>
                    <LoadingRows rows={3} cols={5} />
                  </tbody>
                </table>
              ) : !referrals || referrals.page.content.length === 0 ? (
                <EmptyState
                  icon={<Users className="h-6 w-6" />}
                  title="No referrals"
                  body="This candidate has not shared any invite links yet."
                />
              ) : (
                <>
                  {referrals.stats && (
                    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 text-center">
                        <p className="text-lg font-black text-cyan-400">{referrals.stats.total ?? 0}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">total</p>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 text-center">
                        <p className="text-lg font-black text-emerald-400">{referrals.stats.registered ?? 0}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">registered</p>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 text-center">
                        <p className="text-lg font-black text-amber-400">{referrals.stats.active ?? 0}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">active</p>
                      </div>
                      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 text-center">
                        <p className="text-lg font-black text-rose-400">{referrals.stats.expired ?? 0}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">expired</p>
                      </div>
                    </div>
                  )}
                  <table className="w-full">
                    <thead className="border-y border-slate-800 bg-slate-900/60">
                      <tr>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Invitee</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Used</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Expiry</th>
                        <th className="px-5 py-4 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.page.content.map((item) => (
                        <tr key={item.invitationId} className="border-t border-slate-800/70 transition hover:bg-slate-900/40">
                          <td className="px-5 py-4">
                            <p className="text-xs font-bold text-slate-200">
                              {item.firstName || item.lastName
                                ? `${item.firstName || ""} ${item.lastName || ""}`.trim()
                                : item.email}
                            </p>
                            <p className="text-[10px] font-semibold text-slate-500">{item.email || "-"}</p>
                          </td>
                          <td className="px-5 py-4">
                            <span className="rounded-full border border-slate-700 bg-slate-950/60 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-slate-400">
                              {item.registrationStatus?.replace(/_/g, " ").toLowerCase() || "-"}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-400">
                            {item.used ? "Yes" : "No"}
                          </td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                            {formatDateTime(item.expiryAt)}
                          </td>
                          <td className="px-5 py-4 text-xs font-semibold text-slate-500">
                            {formatDateTime(item.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {referrals.page.totalPages > 1 && (
                    <div className="mt-4">
                      <Pagination {...referrals.page} setPage={setReferralPage} />
                    </div>
                  )}
                </>
              )}
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default SmartAnalyticsCandidateComponent;
